import { GetTokenSilentlyOptions } from '@auth0/auth0-react';
import { Token } from '@/core/auth/model/token';
import { createCredentials, Credentials, CredentialsData } from '@/core/auth/model/credentials';
import { CredentialsBody } from '@/core/auth/model/credentials-body';
import { LocalStorageUtil } from '@/shared/storage/export';
import { PromiseQueue } from '@/shared/util/export';

const localStorage = new LocalStorageUtil();
let tokenObservers: Function[] = [];
const logoutObservers: Array<() => Promise<void> | void> = [];
const apiErrorObservers = new Set<(message: string) => void>();
const permissionsObservers = new Set<() => void>();

const TOKEN_REFRESH_INTERVAL = 3.6e6;
const TOKEN_STORAGE_KEY = 'access_token';
const PERMISSIONS_COOKIE = 'permissions';
const PERMISSIONS_COOKIE_MAX_AGE = 2419200; // 60 * 60 * 24 * 28, four weeks
const AUTH0_READY_TIMEOUT = 10000;

const KMSI_CLAIM = 'https://mnimarkets.com/kmsi_exp';
const KMSI_COOKIE = 'kmsi_exp';

const KMSI_COOKIE_MAX_AGE = 604800; // 60 * 60 * 24 * 7, a week

// Thrown when a capped session has run out. The login page keys off this to
// force the Auth0 form, because resuming by SSO would never re-issue the cap.
export const SESSION_EXPIRED = 'kmsi-session-expired';

const refreshQueue = new PromiseQueue<Credentials | undefined>();

type Auth0TokenGetter = (options?: GetTokenSilentlyOptions) => Promise<string>;
let auth0TokenGetter: Auth0TokenGetter | undefined;
let resolveAuth0Ready: () => void = () => {};
const auth0Ready: Promise<void> = new Promise(resolve => {
  resolveAuth0Ready = resolve;
});

function registerAuth0TokenGetter(getter: Auth0TokenGetter) {
  auth0TokenGetter = getter;
  resolveAuth0Ready();
}

// Cookies on .mnimarkets.com, readable by every MNI app.
function setSharedCookie(name: string, value: string, maxAgeSeconds: number) {
  const isTest = process.env.REACT_APP_TEST_ENV === 'true';
  const attrs = ['path=/', `max-age=${Math.max(0, Math.floor(maxAgeSeconds))}`];
  if (!isTest) {
    attrs.push('domain=.mnimarkets.com');
    attrs.push('secure');
  }
  document.cookie = `${name}=${value}; ${attrs.join('; ')}`;
}

// Reads the live cookie (not a stale react-cookie render snapshot) so callers
// observe writes made during a silent refresh. The pattern is cached because
// this runs on every outbound HTTP request via getAccessToken.
const cookiePatterns: Record<string, RegExp> = {};

function readSharedCookie(name: string): string | undefined {
  const pattern = (cookiePatterns[name] ??= new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  const match = document.cookie.match(pattern);
  return match ? match[1] : undefined;
}

function setPermissionsCookie(permissions: string[]) {
  const value = encodeURIComponent((permissions ?? []).join(','));

  const cap = getKmsiExpiry();
  const maxAge = cap
    ? Math.min(PERMISSIONS_COOKIE_MAX_AGE, (cap - new Date().getTime()) / 1000)
    : PERMISSIONS_COOKIE_MAX_AGE;

  setSharedCookie(PERMISSIONS_COOKIE, value, maxAge);
}

function getPermissionsCookie(): string | undefined {
  const value = readSharedCookie(PERMISSIONS_COOKIE);
  if (value === undefined) return undefined;
  // Never let a malformed percent-encoding throw here — this value gates the
  // auth-provider's stay-logged-in check, so a throw would be a false logout.
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

// The cap lives only in the shared cookie, so every MNI app reads one value and
// a change made in one is visible in the others immediately. The cookie outlives
// the deadline it carries — an expired cap still has to be readable, or an app
// loading after expiry would find nothing and let the session through.
function getKmsiExpiry(): number | undefined {
  const raw = Number(readSharedCookie(KMSI_COOKIE));
  if (!Number.isFinite(raw) || raw <= 0) return undefined;

  // The Auth0 action emits epoch milliseconds; tolerate seconds in case that
  // ever changes, since nothing in this repo pins the unit down.
  return raw < 1e12 ? raw * 1000 : raw;
}

function setKmsiExpiry(expiry: number) {
  const capped = Number.isFinite(expiry) && expiry > 0;
  setSharedCookie(KMSI_COOKIE, capped ? String(expiry) : '0', KMSI_COOKIE_MAX_AGE);
}

function clearKmsiExpiry() {
  setSharedCookie(KMSI_COOKIE, '', 0);
}

// Called with the claim off a freshly minted access token. Absent means the
// token came from an SSO round-trip or a refresh exchange, neither of which says
// anything about the cap — the cookie stays authoritative. A malformed claim is
// ignored rather than published as "no cap", which would wipe another app's cap.
function recordKmsiClaim(claim: unknown) {
  if (claim === undefined) return;

  const value = Number(claim);
  if (Number.isFinite(value)) setKmsiExpiry(value);
}

function kmsiExpired(): boolean {
  const cap = getKmsiExpiry();
  return !!cap && new Date().getTime() > cap;
}

function auth0SessionEnded(credentials: Credentials): boolean {
  if (credentials.token.source !== 'auth0') return false;

  return kmsiExpired() || readSharedCookie(PERMISSIONS_COOKIE) === undefined;
}

let lastForcedRefresh = 0;

function forceRefresh() {
  lastForcedRefresh = new Date().getTime();
  refreshToken(true).catch(error => console.warn('Background token refresh failed', error));
}

setInterval(forceRefresh, TOKEN_REFRESH_INTERVAL);

document.addEventListener('visibilitychange', () => {
  if (
    document.visibilityState === 'visible' &&
    new Date().getTime() - lastForcedRefresh > TOKEN_REFRESH_INTERVAL
  )
    forceRefresh();
});

forceRefresh();

function setCredentials(credentials: CredentialsData | undefined) {
  if (!credentials) localStorage.removeValue(TOKEN_STORAGE_KEY);
  else localStorage.saveValue(TOKEN_STORAGE_KEY, credentials);
}

function getCredentials(): Credentials | undefined {
  const value = localStorage.getValue<CredentialsData>(TOKEN_STORAGE_KEY);

  if (value?.body && value.header && value.token) return createCredentials(value);

  return undefined;
}

async function loginWithAuth0(getAccessTokenSilently: () => Promise<string>): Promise<Token> {
  if (!tokenValid()) {
    console.log('Refreshing auth0 server token');

    const value = await getAccessTokenSilently();

    const { body } = unmarshallJWTToken(value);

    // An interactive login re-states the cap (or lifts it with 0); an SSO
    // round-trip carries no claim and leaves the recorded one in place.
    recordKmsiClaim(body[KMSI_CLAIM]);

    // The SDK keeps its refresh token in localStorage, which outlives a capped
    // session, so the silent exchange above can still succeed after the cap has
    // passed. Refuse it and let the caller start a real login instead.
    if (kmsiExpired()) {
      setCredentials(undefined);
      clearKmsiExpiry();
      throw new Error(SESSION_EXPIRED);
    }

    setPermissionsCookie(body.permissions ?? body.roles ?? []);

    await storeToken({
      source: 'auth0',
      access_token: value,
      expires_in: Math.max(0, body.exp - new Date().getTime() / 1000),
    } as Token);
  }

  return getCredentials()?.token as Token;
}

async function loginWithToken(t: string): Promise<Token> {
  if (!tokenValid()) {
    console.log('Refreshing server token');

    //'https://apis.marketnews.com/api/auth/exchange/token'

    const response = await fetch('https://apis.marketnews.com/api/auth/rm-exchange/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ access_token: t }),
    });

    if (response.status !== 200) throw new Error('Login Failed');

    const token = await response.json();
    token.source = 'remote-token';

    if (token) {
      await storeToken(token);
    }
  }

  return getCredentials()?.token as Token;
}

async function loginWithBbgToken(t: string): Promise<Token> {
  console.log('Refreshing server token');

  //'https://apis.marketnews.com/api/auth/exchange/token'

  const response = await fetch(
    'https://apis.marketnews.com/api/auth/bbg/token?excludeDefaults=true',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ access_token: t }),
    },
  );

  if (response.status !== 200) throw new Error('Login Failed');

  const token = await response.json();
  token.source = 'remote-token';
  token.refresh_expires_in = 0;

  if (token) {
    await storeToken(token);
  }

  return getCredentials()?.token as Token;
}

async function loginWithUserAndPassword(username: string, password: string): Promise<Token> {
  if (!tokenValid()) {
    console.log('Refreshing server token');

    const response = await fetch('https://api.alphaflash.com/api/auth/alphaflash-client/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (response.status !== 200) throw new Error('Login Failed');

    const token = await response.json();
    token.source = 'username-password';

    if (token) {
      await storeToken(token);
    }
  }

  return getCredentials()?.token as Token;
}

async function refreshToken(forceFresh = false): Promise<Credentials | undefined> {
  return refreshQueue.enqueue(async () => {
    const storedCredentials = getCredentials();
    if (!storedCredentials) return undefined;

    // Auth0 logins refresh through the Auth0 SDK using the rotating refresh
    // token (offline_access), not the alphaflash backend. The background timer
    // forces a refresh-token exchange (cacheMode 'off') so updated roles/
    // permissions are picked up; on-demand calls use the cache to avoid an
    // unnecessary network round-trip.
    if (storedCredentials.token.source === 'auth0') {
      // A capped session ends even though the refresh token is still usable.
      if (auth0SessionEnded(storedCredentials)) {
        setCredentials(undefined);
        return undefined;
      }

      let readyTimer: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([
        auth0Ready,
        new Promise(resolve => {
          readyTimer = setTimeout(resolve, AUTH0_READY_TIMEOUT);
        }),
      ]);
      clearTimeout(readyTimer);
      if (!auth0TokenGetter) return undefined;
      try {
        const value = await auth0TokenGetter({ cacheMode: forceFresh ? 'off' : 'on' });
        const { body } = unmarshallJWTToken(value);
        recordKmsiClaim(body[KMSI_CLAIM]);
        setPermissionsCookie(body.permissions ?? body.roles ?? []);
        return storeToken({
          source: 'auth0',
          access_token: value,
          expires_in: Math.max(0, body.exp - new Date().getTime() / 1000),
        } as Token);
      } catch (error) {
        console.warn('Auth0 silent token refresh failed', error);
        return undefined;
      }
    }

    const refresh_token = storedCredentials.token.refresh_token;

    if (refresh_token) {
      const response = await fetch('https://api.alphaflash.com/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });

      if (response.status !== 200) throw new Error('Login Failed');

      const token = await response.json();
      token.source = storedCredentials?.token.source;

      if (token) {
        return storeToken(token);
      }
    }
  });
}

function unmarshallJWTToken(token: string) {
  const [headerString, bodyString] = token.split('.');

  return {
    header: JSON.parse(atob(headerString)),
    body: JSON.parse(atob(bodyString)),
  };
}

function getCredentialsFromToken(token: Token): CredentialsData {
  const [headerString, bodyString] = token.access_token.split('.');

  const body: CredentialsBody = JSON.parse(atob(bodyString));

  if (!body.permissions && body.roles) body.permissions = body.roles;

  return {
    header: JSON.parse(atob(headerString)),
    refresh_expiration: new Date(new Date().getTime() + (token.refresh_expires_in || 0) * 1000),
    access_expiration: new Date(new Date().getTime() + (token.expires_in || 0) * 1000),
    body,
    token,
  };
}

function permissionsKey(permissions: string[] | undefined): string {
  return [...(permissions ?? [])].sort().join('|');
}

function storeToken(token: Token) {
  const previous = getCredentials();

  setCredentials(getCredentialsFromToken(token));

  notifyTokenAvailable(token);

  const stored = getCredentials();

  if (
    previous &&
    permissionsKey(previous.body.permissions) !== permissionsKey(stored?.body.permissions)
  )
    permissionsObservers.forEach(cb => cb());

  return stored;
}

async function logout() {
  await Promise.all(logoutObservers.map(cb => cb()));
  setCredentials(undefined);
}

function onLogout(callback: () => Promise<void> | void) {
  logoutObservers.push(callback);
}

// Lets the HTTP layer report failed API calls (e.g. retries exhausted) to whoever handles
// the session, without depending on the messaging module.
function notifyApiError(message: string) {
  apiErrorObservers.forEach(cb => cb(message));
}

function onApiError(callback: (message: string) => void): () => void {
  apiErrorObservers.add(callback);
  return () => {
    apiErrorObservers.delete(callback);
  };
}

// Fires when a refreshed token carries a different set of permissions.
function onPermissionsChanged(callback: () => void): () => void {
  permissionsObservers.add(callback);
  return () => {
    permissionsObservers.delete(callback);
  };
}

async function onTokenAvailable(): Promise<Token> {
  const stored = await getAccessToken();

  if (stored) return stored;
  else
    return new Promise<Token>(resolve => {
      tokenObservers.push(resolve);
    });
}

function notifyTokenAvailable(token: Token) {
  tokenObservers.forEach(value => value(token));
  tokenObservers = [];
}

async function getAccessToken(): Promise<Token | undefined> {
  const stored = getCredentials();

  if (!stored) return;

  if (auth0SessionEnded(stored)) {
    setCredentials(undefined);
    return;
  }

  if (!stored.accessTokenExpired()) {
    return stored.token;
  } else if (stored.token.source === 'auth0') {
    // Auth0 has no stored refresh token of its own; the SDK holds it.
    return (await refreshToken())?.token;
  } else if (!stored.refreshTokenExpired()) {
    return (await refreshToken())?.token;
  }
}

function tokenValid() {
  const storedCredentials = getCredentials();
  if (!storedCredentials) return false;

  if (auth0SessionEnded(storedCredentials)) return false;

  return !storedCredentials?.accessTokenExpired();
}

function getSubjectData() {
  const storedCredentials = getCredentials();
  if (!storedCredentials) return null;

  const sub = storedCredentials.body.sub;

  if (sub.startsWith('auth0|')) {
    return {
      id: sub,
      provider: 'auth0',
    };
  }

  const realm = storedCredentials.body.realm;

  return {
    id: sub,
    provider: `mni|${realm}`,
  };
}

// eslint-disable-next-line
export default {
  onTokenAvailable,
  getAccessToken,
  logout,
  onLogout,
  notifyApiError,
  onApiError,
  onPermissionsChanged,
  loginWithToken,
  loginWithBbgToken,
  loginWithAuth0,
  getCredentials,
  loginWithUserAndPassword,
  tokenValid,
  getSubjectData,
  registerAuth0TokenGetter,
  getPermissionsCookie,
};
